import { useRef, useState } from 'react';
import { getRelicAbilities } from './relicAbilities';
import PhaseAnchorControl from './PhaseAnchorControl';
import ChainReactionControl from './ChainReactionControl';
import BountyTagControl from './BountyTagControl';
import GhostBumpControl from './GhostBumpControl';
import DecoyBeaconControl from './DecoyBeaconControl';
import RelicIcon from './RelicIcon';
import CoreboundDialog from './CoreboundDialog';
import { playSound } from '../../utils/utils';
import './RelicAbilityBar.css';

let nextBarId = 0;

const RelicAbilityBar = ({ game, context = 'setup', ...actions }) => {
  const [selected, setSelected] = useState(null);
  const barId = useRef(null);
  const buttons = useRef({});
  if (!barId.current) barId.current = `coreboundAbilityBar-${++nextBarId}`;
  const abilities = getRelicAbilities(game, context);
  const active = abilities.find(({ id }) => id === selected);
  if (!abilities.length) return null;
  const close = () => {
    playSound('/sounds/menu-select-3.mp3');
    buttons.current[selected]?.focus({ preventScroll: true });
    setSelected(null);
  };
  const panels = {
    phaseAnchor: <PhaseAnchorControl game={game} />,
    chainReaction: <ChainReactionControl game={game} />,
    ghostBump: <GhostBumpControl game={game} />,
    decoyBeacon: (
      <DecoyBeaconControl game={game} onToggle={actions.onToggleDecoyBeacon} />
    ),
  };
  return (
    <section
      className="coreboundAbilityBar"
      aria-label="Relic abilities"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && active) {
          event.preventDefault();
          close();
        }
      }}
    >
      <header>
        <strong>
          {context === 'warning'
            ? 'COUNTERATTACK ABILITIES'
            : 'RELIC ABILITIES'}
        </strong>
        <span>Tap an icon for details and controls</span>
      </header>
      <div
        className="coreboundAbilityList"
        role="group"
        aria-label="Choose a relic ability"
      >
        {abilities.map((ability) => (
          <button
            key={ability.id}
            ref={(node) => {
              buttons.current[ability.id] = node;
            }}
            type="button"
            className={`coreboundAbilityIcon is-${ability.status.toLowerCase()} ${
              ability.armed ? 'is-armed' : ''
            } ${selected === ability.id ? 'is-selected' : ''}`}
            aria-expanded={selected === ability.id}
            aria-haspopup="dialog"
            aria-controls={
              selected === ability.id ? `${barId.current}-panel` : undefined
            }
            aria-label={`${ability.name}. ${ability.status}. ${
              ability.remaining
            } of 1 use per ${ability.limit}. ${
              ability.reactive ? 'Reactive ability.' : ''
            }`}
            title={`${ability.name} · ${ability.rarity} · ${ability.remaining}/1 per ${ability.limit}`}
            onClick={() => {
              const closing = selected === ability.id;
              playSound(
                closing
                  ? '/sounds/menu-select-3.mp3'
                  : '/sounds/menu-select-2.mp3'
              );
              setSelected(closing ? null : ability.id);
            }}
          >
            <span className="coreboundAbilitySymbol" aria-hidden="true">
              <RelicIcon relicId={ability.id} icon={ability.icon} />
            </span>
            <b>{ability.shortName}</b>
            <span className="coreboundAbilityStatus">
              {ability.status} <small>{ability.remaining}/1</small>
            </span>
          </button>
        ))}
      </div>
      {active && (
        <CoreboundDialog
          id={`${barId.current}-panel`}
          className="coreboundAbilityPanel"
          title={`${active.name} controls`}
          fullScreen={active.id === 'bountyTag'}
          onClose={close}
        >
          {(closeDialog) => (
            <>
              <div className="coreboundAbilityPanelHeader">
                <span>
                  {active.rarity.toUpperCase()} · {active.remaining}/1 per{' '}
                  {active.limit}
                  {active.reactive ? ' · REACTIVE' : ''}
                </span>
              </div>
              {active.reactive ? (
                <p className="coreboundAbilityReactive">
                  {active.used ? `Used this ${active.limit}. ` : ''}
                  {active.id === 'mirrorChip'
                    ? 'After the die settles and tile choices resolve, Mirror Chip automatically reverses the initial roll if the reversed value is higher. Rolls 01–09 become 10–90. It is spent only when it flips a roll.'
                    : 'Last Stand Protocol automatically interrupts a lethal hit with one survival throw. Beat 75 after board and relic bonuses to survive at 1 HP.'}
                </p>
              ) : active.id === 'bountyTag' ? (
                <BountyTagControl
                  game={game}
                  fullScreen
                  onMark={(index) => {
                    actions.onMarkBounty?.(index);
                    closeDialog();
                  }}
                  onClear={actions.onClearBounty}
                />
              ) : (
                panels[active.id]
              )}
            </>
          )}
        </CoreboundDialog>
      )}
    </section>
  );
};

export default RelicAbilityBar;
